// Thin wrapper over the Team Tools weapon balance endpoints.
//
// The weapons, the stats, their bounds and the cross-stat rules all live on
// the server, in gameapi's registry. Nothing here knows what a stat means; it
// carries values and shows back whatever the registry says when it refuses.

const BASE = '/team-tools/api/weapon-balance'

export type StatCell = {
    stat: string
    /** 'int' or 'float'. */
    kind: string
    min: number
    max: number
    range: string
    help: string
    /** What the build compiles in. Null when the registry dump has not landed. */
    default: number | null
    fleet: number | null
    server: number | null
    effective: number | null
    /** Which of the three the effective value came from. */
    source: 'default' | 'fleet' | 'server'
    /** The row on the scope being edited, as typed. Null when there is none. */
    value: string | null
    enabled: boolean
    exists: boolean
    updated_by: string | null
    updated_at: string | null
    /** Stored, but would not survive today's bounds, so it is not delivered. */
    deliverable_error: string | null
}

export type Weapon = {
    weapon_id: number
    name: string
    /** MeleeWeapon, ShooterWeapon, Archery, Defense. The build decides which
     *  stats a weapon has, so this is why one shows two rows and another
     *  thirteen. */
    type: string
    /** The same still the Packs screens use, matched on model name. Null for
     *  a weapon the minted collection does not carry. */
    image_url: string | null
    stats: StatCell[]
}

export type FleetRow = {
    id: number
    name: string | null
    slot: string | null
    echoed: number | null
    /** applied · pending · behind · old build */
    state: string
    /** "<weapon>.<stat>" -> why, as the server reports. Our bounds mirror the
     *  game's, so anything here is drift between the two. */
    refused: Record<string, string>
}

export type ServerRow = { id: number; name: string | null; slot: string | null }

export type WeaponChange = {
    id: number
    action: number
    action_label: string
    scope: string
    weapon_id: number | null
    weapon: string
    stat: string
    old_value: string | null
    new_value: string | null
    revision: number
    by: string
    at: string
}

export type Balance = {
    scope: 'global' | 'server'
    label: string
    server_id: number | null
    /** Why the numbers on this scope were picked. */
    notes: string
    /** Whether "drop this overlay" applies: a server scope with rows on it. */
    removable: boolean
    revision: number
    changed_at: string | null
    /** Which build the defaults came from. Blank until the dump lands. */
    build: string
    weapons: Weapon[]
    /** Cross-stat faults in anything that would ship, fleet or overlay. */
    problems: string[]
    fleet: FleetRow[]
    servers: ServerRow[]
}

const csrf = () =>
    document.cookie
        .split('; ')
        .find((c) => c.startsWith('csrftoken='))
        ?.split('=')[1] ?? ''

async function send(path: string, method: string, body?: unknown) {
    const response = await fetch(`${BASE}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf() },
        body: body === undefined ? undefined : JSON.stringify(body),
    })
    let data: any = {}
    try {
        data = await response.json()
    } catch {
        // A 403 from the permission mixin carries a body; a 500 may not.
    }
    if (!response.ok) {
        throw new Error(data.error || `${method} ${path} failed (${response.status})`)
    }
    return data
}

const scoped = (server: number | null) => (server === null ? '' : `?server=${server}`)

export const getBalance = (server: number | null): Promise<Balance> => send(scoped(server), 'GET')

export const setStat = (
    weaponId: number,
    stat: string,
    body: { value?: string; enabled?: boolean; server?: number | null }
): Promise<{ revision: number; unchanged?: boolean }> =>
    send(`/${weaponId}/${stat}`, 'POST', { ...body, server: body.server ?? undefined })

export const clearStat = (weaponId: number, stat: string, server: number | null) =>
    send(`/${weaponId}/${stat}${scoped(server)}`, 'DELETE')

export const setScope = (body: { server?: number | null; notes?: string }) =>
    send('/scope', 'POST', { ...body, server: body.server ?? undefined })

/** Drop every override on one server, putting it back on the fleet. */
export const clearScope = (server: number) => send(`/scope?server=${server}`, 'DELETE')

export const getWeaponHistory = (weaponId?: number): Promise<{ total: number; changes: WeaponChange[] }> =>
    send(`/history${weaponId ? `?weapon=${weaponId}` : ''}`, 'GET')
